import { useMemo } from 'react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { Badge } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import type { TransactionDto } from './types';
import {
  TRANSACTION_TYPE_LABELS,
  TRANSACTION_TYPE_BADGE_VARIANT,
  STATUS_BADGE_VARIANT,
} from './types';
import { formatAmount, formatDate, formatFxDescription } from './format';

interface HistoryTableProps {
  transactions: TransactionDto[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onRowClick: (transaction: TransactionDto) => void;
  onEventsClick?: (transaction: TransactionDto) => void;
  emptyMessage?: string;
  searchQuery?: string;
  onClearSearch?: () => void;
}

export function HistoryTable({
  transactions,
  isLoading,
  sorting,
  onSortingChange,
  onRowClick,
  onEventsClick,
  emptyMessage,
  searchQuery,
  onClearSearch,
}: HistoryTableProps) {
  const columns = useMemo<ColumnDef<TransactionDto, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ getValue }) => formatDate(getValue() as string),
      },
      {
        accessorKey: 'transaction_type',
        header: 'Type',
        enableSorting: false,
        cell: ({ getValue }) => {
          const type = getValue() as string;
          const label = TRANSACTION_TYPE_LABELS[type] ?? type;
          const variant = TRANSACTION_TYPE_BADGE_VARIANT[type] ?? 'info';
          return <Badge variant={variant}>{label}</Badge>;
        },
      },
      {
        id: 'counterparty',
        header: 'Counterparty',
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original;
          return t.beneficiary_name ?? t.sender_name ?? t.description ?? '-';
        },
      },
      {
        id: 'amount_display',
        header: 'Amount',
        accessorKey: 'amount',
        cell: ({ row }) => {
          const t = row.original;
          if (t.transaction_type === 'fx_trade') {
            return (
              <span className="text-[13px] font-semibold text-[#7b1fa2]">
                {formatFxDescription(t.amount, t.currency, t.rate, t.reference_currency)}
              </span>
            );
          }
          const isIncoming = t.transaction_type === 'incoming';
          return (
            <span className={`font-semibold ${isIncoming ? 'text-[#2e7d32]' : 'text-[#1a1a1a]'}`}>
              {isIncoming ? '+' : ''}
              {formatAmount(t.amount, t.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const variant = STATUS_BADGE_VARIANT[status] ?? 'default';
          return <Badge variant={variant}>{status}</Badge>;
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original;
          const canShowEvents = onEventsClick !== undefined && typeof t.id === 'number';
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick(t);
                }}
                data-testid={`history-detail-btn-${t.transaction_id}`}
              >
                Details
              </Button>
              {canShowEvents && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventsClick(t);
                  }}
                  data-testid={`history-events-btn-${t.transaction_id}`}
                >
                  Events
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [onRowClick, onEventsClick],
  );

  if (!isLoading && transactions.length === 0) {
    return (
      <div
        className="rounded-[10px] bg-white p-12 text-center shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
        data-testid="history-empty-state"
      >
        {searchQuery ? (
          <>
            <p className="text-[14px] text-[#999]">
              No transactions match &apos;{searchQuery}&apos;.
            </p>
            {onClearSearch && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-3"
                onClick={onClearSearch}
                data-testid="history-clear-search"
              >
                Clear Search
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-[14px] text-[#999]">
              {emptyMessage ?? 'No transactions found for this client.'}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={transactions}
      sorting={sorting}
      onSortingChange={onSortingChange}
      isLoading={isLoading}
      rowClick={onRowClick}
      className="data-testid-history-table"
    />
  );
}
